/**
 * メンバー管理の区画（W22・FR16）。`/settings` の中に置く。
 *
 * この面が守っていること:
 * - **押せるかどうかを画面が決めない。** `editableByViewer` はサーバーの
 *   判断そのもので、画面は役割を見て分岐しない。無効にした行には、なぜ
 *   押せないかを 1 行で書く
 * - **読んだ版をそのまま書き戻す。** `expectedVersion` は画面が数え直さず、
 *   一覧が返した `version` を渡す。食い違えば API が `CONFLICT` で断り、
 *   その断りをそのまま出す
 * - **`userErrors` は区画に出す。** 入力欄が無い操作（行の役割の変更・除名）の
 *   断りを欄へ流すと、黙って消える
 * - **除名は確認を挟む。** 押し間違いで人が消える形にしない
 * - **招待の一覧に「招待中の人はいません」を出せるのは、読めたときだけ。**
 *   読めなかったときに空の表を出すと、出したはずの招待が消えたように見える
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  ErrorState,
  formatJstDateTime,
  InvitationsDocument,
  InviteMemberDocument,
  LoadingState,
  MembersDocument,
  RemoveMemberDocument,
  RevokeInvitationDocument,
  SetMemberRoleDocument,
  Tag,
  type TenantRole,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";
import { useViewer } from "../ViewerContext";

/** 画面から選べる役割。**代行はここに出さない**（期限つきの手続きで作る状態）。 */
const ASSIGNABLE_ROLES: readonly TenantRole[] = [
  "TENANT_ADMIN",
  "TENANT_EDITOR",
  "TENANT_VIEWER",
];

interface MemberRow {
  readonly id: string;
  readonly user: {
    readonly displayName: string;
    readonly email?: string | null;
  };
  readonly role: TenantRole;
  readonly joinedAt?: string | null;
  readonly version: number;
  readonly editableByViewer: boolean;
}

/**
 * なぜ押せないか。**推し量らずに、分かる範囲だけを書く。**
 *
 * 自分自身かどうかは画面でも分かる。それ以外（管理者でない・最後の管理者）は
 * 断り方を 1 つにまとめる — 画面が役割の表を写して分岐すると、サーバーの
 * 判断と食い違う。
 */
function disabledReason(member: MemberRow, viewerUserId: string): string {
  if (member.id === viewerUserId) {
    return "自分自身の役割を変えたり、自分を外したりはできません。";
  }
  if (member.role === "TENANT_ADMIN") {
    return "この会社で最後の管理者です。先にほかの人を管理者にしてください。";
  }
  return "この操作ができるのは、この会社の管理者だけです。";
}

export function MembersPanel() {
  const viewerUserId = useViewer().user.id;
  const [result, refetch] = useQuery({
    query: MembersDocument,
    requestPolicy: "cache-and-network" as const,
  });
  // 一覧に効く mutation なので、済んだら一覧を取り直す
  const memberTypenames = useMemo(() => ["Member"], []);
  const [setRoleResult, setMemberRole] = useMutation(SetMemberRoleDocument);
  const [removeResult, removeMember] = useMutation(RemoveMemberDocument);
  const [busyMemberId, setBusyMemberId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const members = (result.data?.members ?? []) as readonly MemberRow[];
  const roleMatrix = result.data?.roleMatrix ?? [];
  const userErrors = [
    ...(setRoleResult.data?.setMemberRole.userErrors ?? []),
    ...(removeResult.data?.removeMember.userErrors ?? []),
  ];

  async function changeRole(
    member: MemberRow,
    role: TenantRole,
  ): Promise<void> {
    if (role === member.role) return;
    setBusyMemberId(member.id);
    await setMemberRole(
      {
        input: {
          memberId: member.id,
          role,
          // 画面が数え直さず、読んだ版をそのまま渡す
          expectedVersion: member.version,
        },
      },
      { additionalTypenames: memberTypenames },
    );
    setBusyMemberId(null);
    refetch({ requestPolicy: "network-only" });
  }

  async function remove(member: MemberRow): Promise<void> {
    setBusyMemberId(member.id);
    await removeMember(
      {
        input: { memberId: member.id, expectedVersion: member.version },
      },
      { additionalTypenames: memberTypenames },
    );
    setBusyMemberId(null);
    setConfirmingId(null);
    refetch({ requestPolicy: "network-only" });
  }

  if (result.error !== undefined) {
    return (
      <ErrorState
        title="メンバーを読み込めませんでした"
        description={result.error.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }

  if (result.data === undefined) {
    return <LoadingState label="メンバーを読み込んでいます" />;
  }

  return (
    <div className="vd-stack">
      <Card
        title="メンバー"
        note="この会社の管理画面を使っている人です。招待中の人は下の「招待中の人」に出ます。"
        extra={<HelpIcon slug="settings/members" anchor="メンバー" />}
      >
        <div className="vd-stack">
          {userErrors.length === 0 ? null : (
            <div className="vd-state vd-state--error" role="alert">
              <span className="vd-state-title">操作できませんでした</span>
              {userErrors.map((error) => (
                <span key={`${error.code}-${error.message}`}>
                  {error.message}
                </span>
              ))}
            </div>
          )}

          <div className="vd-table-scroll">
            <table className="vd-table">
              <thead>
                <tr>
                  <th>名前</th>
                  <th>メールアドレス</th>
                  <th>役割</th>
                  <th>参加日</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  const busy =
                    busyMemberId === member.id ||
                    setRoleResult.fetching ||
                    removeResult.fetching;
                  const joinedAt = formatJstDateTime(member.joinedAt);
                  return (
                    <tr key={member.id} data-testid="member-row">
                      <td>{member.user.displayName}</td>
                      <td>{member.user.email ?? "—"}</td>
                      <td>
                        {/* 見出しは表の列にあるので、読み上げ用の名前だけ添える */}
                        <select
                          aria-label={`${member.user.displayName} の役割`}
                          value={member.role}
                          disabled={!member.editableByViewer || busy}
                          onChange={(event) => {
                            void changeRole(
                              member,
                              event.target.value as TenantRole,
                            );
                          }}
                        >
                          {ASSIGNABLE_ROLES.map((role) => (
                            <option key={role} value={role}>
                              {roleMatrix.find((row) => row.role === role)
                                ?.label ?? role}
                            </option>
                          ))}
                          {ASSIGNABLE_ROLES.includes(member.role) ? null : (
                            // 代行など、画面から選べない役割もそのまま見せる
                            <option value={member.role}>
                              {roleMatrix.find(
                                (row) => row.role === member.role,
                              )?.label ?? member.role}
                            </option>
                          )}
                        </select>
                      </td>
                      <td>{joinedAt === "" ? "—" : joinedAt}</td>
                      <td>
                        {confirmingId === member.id ? (
                          <span className="vd-stack">
                            <span>
                              {`${member.user.displayName} をこの会社から外します。よろしいですか。`}
                            </span>
                            <span>
                              <Button
                                variant="primary"
                                disabled={busy}
                                onClick={() => {
                                  void remove(member);
                                }}
                              >
                                はい、外す
                              </Button>{" "}
                              <Button
                                disabled={busy}
                                onClick={() => {
                                  setConfirmingId(null);
                                }}
                              >
                                やめる
                              </Button>
                            </span>
                          </span>
                        ) : (
                          <Button
                            disabled={!member.editableByViewer || busy}
                            onClick={() => {
                              setConfirmingId(member.id);
                            }}
                          >
                            外す
                          </Button>
                        )}
                        {member.editableByViewer ? null : (
                          <p className="vd-stat-note" style={{ margin: 0 }}>
                            {disabledReason(member, viewerUserId)}
                          </p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      <InvitationsPanel />

      <Card
        title="役割でできること"
        extra={
          <span className="vd-row">
            <Tag>参考</Tag>
            <HelpIcon slug="settings/members" anchor="役割でできること" />
          </span>
        }
      >
        {/*
          断り書きは**表の上**に置く。表の下に置いていたときは、行の「できる」
          だけを読んだ人が「編集者は配信を始められる」と受け取ってしまった
          （上の「配信の開始・停止を押せる人」が管理者だけになっていても、
          この表には「できる」と出る）。

          **表の値そのものは変えない。** これは役割が持ちうる上限で、
          サーバーの `decide()` の答えそのものである。画面用に第 2 の表を
          書くと、判断が 2 か所になって片方だけ古くなる。
        */}
        <p className="vd-stat-note" style={{ marginTop: 0 }}>
          ここに出るのは<strong>役割そのものの上限</strong>
          です。実際に押せるかは、
          この表に加えて会社の設定（配信の開始・停止を押せる人）も見ます。
        </p>
        <div className="vd-table-scroll">
          <table className="vd-table">
            <thead>
              <tr>
                <th>役割</th>
                <th>見る</th>
                <th>直す</th>
                <th>配信の開始・停止</th>
              </tr>
            </thead>
            <tbody>
              {roleMatrix.map((row) => (
                <tr key={row.role} data-testid="role-matrix-row">
                  <td>{row.label}</td>
                  <td>{row.canRead ? "できる" : "できない"}</td>
                  <td>{row.canWrite ? "できる" : "できない"}</td>
                  <td>{row.canRunStop ? "できる" : "できない"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/** 画面に出す招待 1 件。 */
interface InvitationRow {
  readonly id: string;
  readonly email: string;
  readonly role: TenantRole;
  readonly status: string;
  readonly expiresAt: string;
}

/**
 * 招待の区画（W22・FR16）。
 *
 * この面が守っていること:
 * - **断りは区画に出す。** 宛先の欄に流すだけだと、行の「取り消す」の断りが
 *   黙って消える
 * - **空と読めなかったを分ける。** 読めなかったときに「招待中の人はいません」
 *   と出すと、出したはずの招待が消えたように見える
 * - **トークンを画面に出さない。** 受け取りのリンクはメールの本文にしか無い。
 *   画面から再送やコピーができるように見せない（できないので）
 */
export function InvitationsPanel() {
  const [result, refetch] = useQuery({
    query: InvitationsDocument,
    requestPolicy: "cache-and-network" as const,
  });
  // 一覧に効く mutation なので、済んだら一覧を取り直す
  const invitationTypenames = useMemo(() => ["Invitation"], []);
  const [inviteResult, inviteMember] = useMutation(InviteMemberDocument);
  const [revokeResult, revokeInvitation] = useMutation(
    RevokeInvitationDocument,
  );
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TenantRole>("TENANT_EDITOR");
  const [busyId, setBusyId] = useState<string | null>(null);

  const invitations = (result.data?.invitations ??
    []) as readonly InvitationRow[];
  // 役割の呼び名はサーバーの表から引く(画面に第 2 の表を書かない)
  const roleLabel = (value: TenantRole): string =>
    result.data?.roleMatrix.find((row) => row.role === value)?.label ?? value;
  const userErrors = [
    ...(inviteResult.data?.inviteMember.userErrors ?? []),
    ...(revokeResult.data?.revokeInvitation.userErrors ?? []),
  ];
  const busy = inviteResult.fetching || revokeResult.fetching;

  async function invite(): Promise<void> {
    const trimmed = email.trim();
    if (trimmed.length === 0) return;
    const outcome = await inviteMember(
      { input: { email: trimmed, role } },
      { additionalTypenames: invitationTypenames },
    );
    // 断られたときは入力を残す（打ち直させない）
    if ((outcome.data?.inviteMember.userErrors ?? []).length === 0) {
      setEmail("");
    }
    refetch({ requestPolicy: "network-only" });
  }

  async function revoke(invitation: InvitationRow): Promise<void> {
    setBusyId(invitation.id);
    await revokeInvitation(
      { input: { invitationId: invitation.id } },
      { additionalTypenames: invitationTypenames },
    );
    setBusyId(null);
    refetch({ requestPolicy: "network-only" });
  }

  if (result.error !== undefined) {
    return (
      <ErrorState
        title="招待を読み込めませんでした"
        description={result.error.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }

  return (
    <Card
      title="招待中の人"
      note="招待のメールに書かれたリンクから登録すると、この会社のメンバーになります。リンクは 7 日で切れます。"
      extra={<HelpIcon slug="settings/members" anchor="招待中の人" />}
    >
      <div className="vd-stack">
        {userErrors.length === 0 ? null : (
          <div className="vd-state vd-state--error" role="alert">
            <span className="vd-state-title">招待できませんでした</span>
            {userErrors.map((error) => (
              <span key={`${error.code}-${error.message}`}>
                {error.message}
              </span>
            ))}
          </div>
        )}

        <div className="vd-stack">
          <label htmlFor="invite-email">メールアドレス</label>
          <input
            id="invite-email"
            type="email"
            value={email}
            disabled={busy}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <label htmlFor="invite-role">役割</label>
          <select
            id="invite-role"
            value={role}
            disabled={busy}
            onChange={(event) => {
              setRole(event.target.value as TenantRole);
            }}
          >
            {ASSIGNABLE_ROLES.map((value) => (
              <option key={value} value={value}>
                {roleLabel(value)}
              </option>
            ))}
          </select>
          <span>
            <Button
              variant="primary"
              disabled={busy || email.trim().length === 0}
              onClick={() => {
                void invite();
              }}
            >
              招待する
            </Button>
          </span>
        </div>

        {result.data === undefined ? (
          <LoadingState label="招待を読み込んでいます" />
        ) : invitations.length === 0 ? (
          <p className="vd-stat-note" style={{ marginBottom: 0 }}>
            招待中の人はいません。
          </p>
        ) : (
          <div className="vd-table-scroll">
            <table className="vd-table">
              <thead>
                <tr>
                  <th>メールアドレス</th>
                  <th>役割</th>
                  <th>期限</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((invitation) => {
                  const expiresAt = formatJstDateTime(invitation.expiresAt);
                  return (
                    <tr key={invitation.id} data-testid="invitation-row">
                      <td>{invitation.email}</td>
                      <td>{roleLabel(invitation.role)}</td>
                      <td>
                        {invitation.status === "EXPIRED"
                          ? "期限切れ"
                          : expiresAt === ""
                            ? "—"
                            : expiresAt}
                      </td>
                      <td>
                        <Button
                          disabled={busy || busyId === invitation.id}
                          onClick={() => {
                            void revoke(invitation);
                          }}
                        >
                          取り消す
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}
